import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Skeleton, Stack, Tooltip, Typography } from "@altimateai/lego";
import { IconInfoCircle } from "@tabler/icons-react";
import { DateRange } from "@/components/dateRange";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { FilterType, TableHeaderType, parseSortDirection } from "@/components/tables/types";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import { FilterApplicableFor } from "@/components/tags/types";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useAppState } from "@/modules/app/useAppContext";
import { formatCost } from "@/helpers/utils";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { getAIServicesSummary, getAiServicesCost } from "@/helpers/apis";
import { DatastoreTypeEnum } from "@/pages/datastores/types";
import type { AIServiceSummaryItem } from "./types";
import { AIServiceType } from "./types";
import { AIServicesCostGraph } from "./AIServicesCostGraph";
import { getServiceByType } from "./constants";
import { useBreadcrumb } from "@/components/breadcrumbs";

export const AIServicesPageContent: FC = () => {
  useBreadcrumb("AI Services", {
    defaultParents: [{ label: "Code", pathname: "/query" }],
    truncateTo: "/query",
  });
  const { currency, datastores = [] } = useAppState();
  const { startDate, endDate, onUpdate } = useDateFromSearchParams();
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const { isInitialized, registerFilters, getFilterValue } = useDatatableContext();
  const [aggregateBy, setAggregateBy] = useState("day");

  const instanceOptions = useMemo(
    () =>
      datastores
        .filter(d => d.type === DatastoreTypeEnum.SNOWFLAKE && d.instance_id != null)
        .map(d => ({ label: d.name, value: String(d.instance_id) })),
    [datastores]
  );

  useEffect(() => {
    registerFilters({
      page: { label: "Page" },
      pageSize: { label: "Page Size" },
      sortAttribute: { label: "Sort Attribute" },
      sortOrder: { label: "Sort Order" },
    });
  }, [registerFilters]);

  const instanceRegisteredRef = useRef(false);
  useEffect(() => {
    if (instanceRegisteredRef.current || instanceOptions.length === 0) return;
    instanceRegisteredRef.current = true;
    registerFilters({
      instance: {
        label: "Instance",
        searchKey: "instance",
        filterType: FilterType.DROPDOWN_SINGLE,
        options: instanceOptions,
      },
    });
  }, [registerFilters, instanceOptions]);

  const instance = getFilterValue<string>("instance");

  const page = parseInt(searchParams.get("page") || "0", 10);
  const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
  const sortAttribute = searchParams.get("sortAttribute") || "total_cost";
  const sortOrder = parseSortDirection(searchParams.get("sortOrder"), "desc");

  const dateParams = useMemo(
    () => ({
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() ?? "",
    }),
    [startDate, endDate]
  );

  // Fetch cost graph data
  const { data: graphData, isLoading: isGraphLoading } = useQuery({
    queryKey: ["ai_services_cost_graph", dateParams, aggregateBy, instance],
    queryFn: () =>
      getAiServicesCost({
        ...dateParams,
        aggregation_level: aggregateBy as "day" | "week" | "month",
        instance_id: instance,
      }),
    enabled: isInitialized,
  });

  // Fetch services summary table data (server-side sorting)
  const { data: summaryData, isFetching: isSummaryFetching } = useQuery({
    queryKey: ["ai_services_summary", dateParams, sortAttribute, sortOrder, instance],
    queryFn: () =>
      getAIServicesSummary({
        ...dateParams,
        sort_by: sortAttribute,
        sort_order: sortOrder,
        instance_id: instance,
      }),
    placeholderData: keepPreviousData,
    enabled: isInitialized,
  });

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateSearchParamsByKey({ page: String(newPage) });
    },
    [updateSearchParamsByKey]
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      updateSearchParamsByKey({ pageSize: String(newSize), page: "0" });
    },
    [updateSearchParamsByKey]
  );

  const services = useMemo(() => {
    if (!summaryData) return [];
    const apiServices = summaryData.services;
    const presentTypes = new Set(apiServices.map(s => s.service_type));
    const allServiceTypes = Object.values(AIServiceType);
    const missingServices: AIServiceSummaryItem[] = allServiceTypes
      .filter(type => !presentTypes.has(type))
      .map(type => ({
        service_type: type,
        service_name: getServiceByType(type)?.displayName ?? type,
        total_cost: 0,
        total_credits: 0,
        record_count: 0,
        percentage_of_total: 0,
      }));
    return [...apiServices, ...missingServices];
  }, [summaryData]);

  const paginatedServices = useMemo(() => {
    const start = page * pageSize;
    return services.slice(start, start + pageSize);
  }, [services, page, pageSize]);

  const getRowUrl = useCallback(
    (item: AIServiceSummaryItem): string =>
      `/query/ai-services/${item.service_type}?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`,
    [startDate, endDate]
  );

  const tableHeaders: TableHeaderType<AIServiceSummaryItem>[] = useMemo(
    () => [
      {
        id: 1,
        label: "Service Name",
        uid: "service_name",
        render: (item: AIServiceSummaryItem) => (
          <span className="al-text-primary">{item.service_name}</span>
        ),
        sort: {
          sortSearchKey: "sortAttribute",
        },
      },
      {
        id: 2,
        label: "Total Cost",
        uid: "total_cost",
        tooltipInfo: "Total cost for the selected date range",
        render: (item: AIServiceSummaryItem) => (
          <Tooltip title={String(item.total_cost)}>
            <span>{formatCost(item.total_cost, currency)}</span>
          </Tooltip>
        ),
        sort: {
          sortSearchKey: "sortAttribute",
        },
      },
    ],
    [currency]
  );

  return (
    <Stack direction="column" className="al-bg-white al-p-3 al-rounded-lg al-mt-3">
      <Stack align="center" justify="between" className="al-w-full">
        <Stack direction="row" align="center" gap="sm">
          <Typography variant="page-heading">AI Services</Typography>
          <Tooltip title="AI Services not surfaced through the Snowflake views will not be reflected in the breakdown">
            <span className="al-text-light-gray al-cursor-help">
              <IconInfoCircle size={18} />
            </span>
          </Tooltip>
        </Stack>
        <Stack direction="row" align="center" gap="md">
          <DateRange
            variant="inline"
            startDate={startDate}
            endDate={endDate}
            onDateRangeSelect={onUpdate}
          />
          <div id="ai-services-filter-container" />
        </Stack>
      </Stack>
      <Typography variant="subheading" className="al-text-muted-foreground">
        Cost breakdown for Snowflake AI Services that are exposed via Snowflake&apos;s native views.
      </Typography>

      {isSummaryFetching && !summaryData ? (
        <Skeleton className="al-h-10 al-w-[512px] al-mt-3" />
      ) : summaryData ? (
        <Typography variant="h4" className="al-p-2 al-rounded al-bg-bg-brand al-mt-3">
          Total Cost (for the selected time period):{" "}
          <span className="al-text-brand">{formatCost(summaryData.total_cost, currency)}</span>
        </Typography>
      ) : null}

      <AIServicesCostGraph
        graphData={graphData}
        isLoading={isGraphLoading}
        aggregateBy={aggregateBy}
        onAggregateChange={setAggregateBy}
        currency={currency}
      />

      {/* Services Table */}
      <Stack direction="column" className="al-mt-4">
        <Typography variant="section-heading" className="al-mb-2">
          All Services
        </Typography>
        <DataTable
          header={tableHeaders}
          data={{ items: paginatedServices }}
          idKey={(item: AIServiceSummaryItem) => item.service_type}
          isLoading={isSummaryFetching}
          getRowUrl={getRowUrl}
          defaultSort={{ sortParamValue: "total_cost", order: "desc" }}
          applicableFor={FilterApplicableFor.AI_SERVICE_USAGE}
          portals={{ filter: "ai-services-filter-container" }}
        />
        <Paginate
          itemCount={services.length}
          numPages={Math.ceil(services.length / pageSize)}
          page={page}
          pageSize={pageSize}
          onPageClick={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          isLoading={isSummaryFetching}
          showPageSize
        />
      </Stack>
    </Stack>
  );
};
